import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Autosuggest from 'react-autosuggest';

import debouce from 'lodash/debounce';

import updateSuggestions from '../actions/action-auto-complete';

import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';
import { trackLink } from '../../lib/analytics-helper';
import getHistory from '../../lib/HistoryHelper';

const searchPlaceHolder = {
  largeScreen: i18n.message('smarthelp/heroSection.searchbox.placeholderText.largeScreen'),
  smallScreen: i18n.message('smarthelp/heroSection.searchbox.placeholderText.smallScreen'),
  hcrPlaceHolder: i18n.message('smarthelp/heroSection.searchbox.placeholderText.hcrPlaceHolder'),
};

const viewSearchResult = i18n.message('smarthelp/heroSection.searchbox.viewSearchResult');

const label = {
  search: i18n.message('smarthelp/heroSection.searchbox.searchBtnText'),
};

const cobrand = Page.getCountry().toLowerCase();

class SearchBox extends Component {
  // Autosuggest propTypes expects this function to be present
  static onSuggestionsClearRequested() {
  }

  static getSearchIcon() {
    return 'vx_icon vx_icon-small vx_icon-magnifying-glass';
  }

  static getPlaceHolderText() {
    if (Page.isMobileLayout()) {
      return searchPlaceHolder.smallScreen;
    }
    return searchPlaceHolder.hcrPlaceHolder;
  }

  // render HCR suggestions followed by View all Results
  static renderSuggestion(faq) {
    if (faq.title === viewSearchResult) {
      return (
        <div className="viewSearchResult">{viewSearchResult}</div>
      );
    }
    return (
      <div className="result-item">{faq.title}</div>
    );
  }
  static addViewResultIntoSuggestion(suggestions) {
    const submitSearchResult = {
      title: viewSearchResult,
    };
    if (suggestions.length > 0 && suggestions.every(suggestion => suggestion.title !== viewSearchResult)) {
      suggestions.push(submitSearchResult);
    }
  }
  constructor(props) {
    super(props);

    this.state = {
      inputText: '',
    };

    this.MIN_LENGTH = 1; // min characters required for search

    this.submitForm = this.submitForm.bind(this);
    this.debouncedUpdateSuggestions = debouce(this.props.updateSuggestions, 300);

    // action-auto-complete functions
    this.onChange = this.onChange.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);

    // view all result getting the state inputText
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.handleClearKeyPress = this.handleClearKeyPress.bind(this);
    this.renderClearButton = this.renderClearButton.bind(this);
    this.clearSearch = this.clearSearch.bind(this);
  }

  componentWillReceiveProps(props) {
    if (props.q !== this.props.q) {
      this.setState({
        inputText: props.q,
      });
    }
  }

  onChange(event, { newValue }) {
    if (event.type === 'change') {
      this.setState({
        inputText: newValue,
      });
    }
  }

  onSuggestionsFetchRequested({ value }) {
    const sanitizedValue = Page.getSanitizedValue(value);
    const sanitizedInputText = Page.getSanitizedValue(this.state.inputText);
    if (sanitizedInputText !== sanitizedValue && sanitizedValue.length > this.MIN_LENGTH) {
      this.debouncedUpdateSuggestions(sanitizedValue);
    }
  }

  onSuggestionSelected(event, { suggestion }) {
    event.preventDefault();
    event.stopPropagation();
    if (suggestion.title === viewSearchResult) {
      trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-search-term', srch: `${this.state.inputText}` });
      this.goToSearchResult();
    } else {
      const title = Page.getFaqUrlFromTitle(suggestion.title);
      const faq = suggestion.docid.toLowerCase();

      trackLink({ id: `${this.props.FPTIId}`, link: `smarthelp-selected-suggested-keyword|${title}` });

      window.location = `/${cobrand}/smarthelp/article/${title}-${faq}?app=searchAutoComplete`;
    }
  }

  getSuggestionValue(faq) {
    if (faq.title === viewSearchResult) {
      return this.state.inputText;
    }
    return faq.title;
  }

  goToSearchResult() {
    const q = Page.getSanitizedValue(this.state.inputText);
    const { channel } = this.props.queryParams;
    const channelQueryParam = (channel) ? `&channel=${channel}` : '';
    const searchUrl = `search?q=${q}${channelQueryParam}`;

    // Redirect to Search Page, if its from Channel Steering.
    if (Page.getPageName() === 'contact-us') {
      const searchBaseUrl = Page.getBaseURLWithExperience();
      window.location = `/${searchBaseUrl}/${searchUrl}`;
    } else {
      const history = getHistory();
      history.push(`/${searchUrl}`);
    }
  }

  submitForm(event) {
    event.preventDefault();
    if (this.state.inputText) {
      // Empty search text will not fire FPTI request
      trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-search-term', srch: `${this.state.inputText}` });
    }

    const searchText = Page.getSanitizedValue(this.state.inputText);
    if (searchText.length < this.MIN_LENGTH) {
      return;
    }

    this.goToSearchResult();
  }

  handleClearKeyPress(event) {
    if (event.key === 'Enter') {
      this.clearSearch();
    }
  }

  clearSearch() {
    // FPTI event for clearing search
    trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-hcr-clear-search' });
    this.setState({
      inputText: '',
    });
  }
  renderClearButton() {
    if (!this.state.inputText.trim()) return null;

    return (<div // eslint-disable-line jsx-a11y/no-static-element-interactions
      className="cw_icon cw_icon-small cw_icon-close-small clear-btn"
      tabIndex="0"
      role="button"
      aria-label="clear search"
      onKeyPress={this.handleClearKeyPress}
      onClick={this.clearSearch}
    />);
  }
  render() {
    this.inputProps = {
      placeholder: SearchBox.getPlaceHolderText(),
      value: this.state.inputText || '',
      onChange: this.onChange,
      className: 'search-input',
      maxLength: 250,
      id: 'help-input-text',
    };

    const sanitizedText = Page.getSanitizedValue(this.state.inputText);
    const suggestions = sanitizedText.length > this.MIN_LENGTH ? this.props.searchResults : [];
    SearchBox.addViewResultIntoSuggestion(suggestions);

    return (
      <div className="search-box">
        <form role="search">
          <div className="search-box-contents">
            <label htmlFor="help-input-text" className="label-help-input-text sr-only">{SearchBox.getPlaceHolderText()}</label>
            <Autosuggest
              suggestions={suggestions}
              onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
              onSuggestionsClearRequested={SearchBox.onSuggestionsClearRequested}
              onSuggestionSelected={this.onSuggestionSelected}
              getSuggestionValue={this.getSuggestionValue}
              renderSuggestion={SearchBox.renderSuggestion}
              inputProps={this.inputProps}
            />
            {this.renderClearButton()}
            <button type="submit" className="search-btn" alt="Search" value={label.search} onClick={this.submitForm}>
              <span className={SearchBox.getSearchIcon()} />
              <span className="hidden">{label.search}</span>
            </button>
          </div>
        </form>
      </div>
    );
  }
}

const suggestionItem = PropTypes.shape({
  title: PropTypes.string,
  docid: PropTypes.string,
});

SearchBox.propTypes = {
  q: PropTypes.string.isRequired,
  queryParams: PropTypes.shape({
    channel: PropTypes.string,
  }),
  updateSuggestions: PropTypes.func.isRequired,
  searchResults: PropTypes.arrayOf(suggestionItem).isRequired,
  FPTIId: PropTypes.string,
};

SearchBox.defaultProps = {
  q: '',
  queryParams: {},
  FPTIId: 'homePage',
};

function mapStateToProps(state) {
  return {
    q: state.searchResult.q,
    queryParams: {
      channel: state.query.params.channel,
    },
    searchResults: state.autoComplete.searchResults,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ updateSuggestions }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchBox);
